import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import BreadcrumbsNav from '../components/BreadcrumbsNav'
import PagePrototypeService from '../services/PagePrototypeService'
import isPrototypesEnabled from '../utils/prototypesEnabled'
import { useProject } from '../context/ProjectContext'
import { useAuth } from '../context/AuthContext'

const MAX_BLOCK_DESCRIPTION_LENGTH = 500
const OVERVIEW_BLOCK_TYPE = 'page_overview'
const BLOCK_STATUS_DRAFT = 'draft'
const BLOCK_STATUS_APPROVED = 'approved'

const emptyIntent = () => ({
  positive: '',
  negative: '',
})

const toIntentText = (value) => (value === null || value === undefined ? '' : String(value))

const hasMeaningfulIntentText = (value) => toIntentText(value).trim().length > 0

const normalizeBlockIntent = (value) => {
  if (value === null || value === undefined) {
    return emptyIntent()
  }

  if (typeof value === 'string') {
    return {
      positive: toIntentText(value),
      negative: '',
    }
  }

  if (typeof value !== 'object' || Array.isArray(value)) {
    return {
      positive: toIntentText(value),
      negative: '',
    }
  }

  return {
    positive: toIntentText(value?.positive),
    negative: toIntentText(value?.negative),
  }
}

const toIntentPayload = (value) => {
  const normalized = normalizeBlockIntent(value)
  if (
    !hasMeaningfulIntentText(normalized.positive) &&
    !hasMeaningfulIntentText(normalized.negative)
  ) {
    return null
  }

  return {
    positive: hasMeaningfulIntentText(normalized.positive)
      ? normalized.positive
      : null,
    negative: hasMeaningfulIntentText(normalized.negative)
      ? normalized.negative
      : null,
  }
}

const formatBlockIntentPreview = (value, maxLength = 240) => {
  const normalized = normalizeBlockIntent(value)
  const parts = []

  if (normalized.positive) {
    parts.push(`positive: ${normalized.positive}`)
  }

  if (normalized.negative) {
    parts.push(`negative: ${normalized.negative}`)
  }

  const text = parts.join(' | ')
  return shorten(text, maxLength)
}

const emptyBlock = (order) => ({
  id: null,
  block_key: '',
  title: '',
  description: '',
  intent: emptyIntent(),
  block_type: '',
  status: BLOCK_STATUS_DRAFT,
  was_interacted: false,
  sort_order: order,
})

const emptyForm = {
  name: '',
  intent: '',
  screenshot_url: '',
  dom_snapshot_url: '',
  page_overview: '',
}

const emptySearchBlock = (order) => ({
  id: `search-${order}`,
  title: '',
  description: '',
  kind: 'block',
})

const toSearchQueryBlocksFromEditor = (form, blocks) => {
  const queryBlocks = []

  ;(blocks || []).forEach((block) => {
    const title = String(block?.title || '').trim()
    const description = String(block?.description || '').trim()

    if (!title && !description) {
      return
    }

    queryBlocks.push({
      id: `search-${queryBlocks.length + 1}`,
      title,
      description,
      kind: 'block',
    })
  })

  return queryBlocks.length ? queryBlocks : [emptySearchBlock(1)]
}

const buildPrototypeSearchText = (prototype) => {
  const parts = []
  const visited = new WeakSet()

  if (prototype?.id) {
    const prototypeId = String(prototype.id)
    parts.push(prototypeId, `prototype ${prototypeId}`, `prototype_id ${prototypeId}`)
  }

  ;(prototype?.blocks || []).forEach((block) => {
    if (!block?.id) {
      return
    }

    const blockId = String(block.id)
    parts.push(blockId, `block ${blockId}`, `block_id ${blockId}`)
  })

  const collect = (value) => {
    if (value === null || value === undefined) {
      return
    }

    const valueType = typeof value
    if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
      parts.push(String(value))
      return
    }

    if (Array.isArray(value)) {
      value.forEach((item) => collect(item))
      return
    }

    if (valueType === 'object') {
      if (visited.has(value)) {
        return
      }
      visited.add(value)
      Object.values(value).forEach((item) => collect(item))
    }
  }

  collect(prototype)
  return parts.join(' ').toLowerCase()
}

const shorten = (value, maxLength = 140) => {
  const text = String(value || '').trim()
  if (!text) return '—'
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 1)}…`
}

const formatPrototypeSource = (prototype) => {
  const sourceType = String(prototype?.source_type || 'manual').trim() || 'manual'
  if (sourceType !== 'journey') {
    return 'manual'
  }

  const journeyId = String(prototype?.source_journey_id || '').trim()
  if (!journeyId) {
    return 'journey'
  }

  return `journey: ${journeyId}`
}

const countApprovedBlocks = (prototype) =>
  (prototype?.blocks || []).filter((block) => block?.status === BLOCK_STATUS_APPROVED).length

const getPrototypeCategory = (prototype) =>
  countApprovedBlocks(prototype) > 0 ? 'Knowledge base' : 'Candidate'

const extractOverview = (prototype) =>
  prototype?.blocks?.find((block) => block?.block_type === OVERVIEW_BLOCK_TYPE)
    ?.description || ''

const countInteractedBlocks = (prototype) =>
  (prototype?.blocks || []).filter((block) => block?.was_interacted).length

const collectApprovedBlockTypes = (prototypes = []) => {
  const unique = new Map()

  ;(prototypes || []).forEach((prototype) => {
    ;(prototype?.blocks || []).forEach((block) => {
      if (block?.status !== BLOCK_STATUS_APPROVED) {
        return
      }

      const blockType = String(block?.block_type || '').trim()
      if (!blockType) {
        return
      }

      const normalizedKey = blockType.toLowerCase()
      if (!unique.has(normalizedKey)) {
        unique.set(normalizedKey, blockType)
      }
    })
  })

  return Array.from(unique.values()).sort((a, b) => a.localeCompare(b))
}

const toEditableBlocks = (prototype) => {
  const normalized = (prototype?.blocks || [])
    .filter((block) => block?.block_type !== OVERVIEW_BLOCK_TYPE)
    .sort((a, b) => {
      const interactedOrder = Number(Boolean(b?.was_interacted)) - Number(Boolean(a?.was_interacted))
      if (interactedOrder !== 0) {
        return interactedOrder
      }

      return (a?.sort_order || 0) - (b?.sort_order || 0)
    })
    .map((block, index) => ({
      id: block?.id || null,
      block_key: block?.block_key || '',
      title: block?.title || '',
      description: block?.description || '',
      intent: normalizeBlockIntent(block?.intent),
      block_type: block?.block_type || '',
      status: block?.status || BLOCK_STATUS_DRAFT,
      was_interacted: Boolean(block?.was_interacted),
      sort_order: index + 1,
    }))

  return normalized.length ? normalized : [emptyBlock(1)]
}

const PagePrototypes = () => {
  const { user } = useAuth()
  const prototypesEnabled = isPrototypesEnabled(user?.email)
  const { selectedProject } = useProject()

  const [form, setForm] = useState({
    ...emptyForm,
  })
  const [blocks, setBlocks] = useState([emptyBlock(1)])
  const [screenshotFile, setScreenshotFile] = useState(null)
  const [domJsonFile, setDomJsonFile] = useState(null)
  const [urlsFile, setUrlsFile] = useState(null)
  const [editingPrototypeId, setEditingPrototypeId] = useState(null)

  const [prototypes, setPrototypes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkError, setBulkError] = useState('')
  const [bulkResult, setBulkResult] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [tablePage, setTablePage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortDirection, setSortDirection] = useState('desc')
  const [showCandidatePrototypes, setShowCandidatePrototypes] = useState(false)
  const [vectorSearchBlocks, setVectorSearchBlocks] = useState([emptySearchBlock(1)])
  const [vectorSearchLoading, setVectorSearchLoading] = useState(false)
  const [vectorSearchError, setVectorSearchError] = useState('')
  const [blockSearchResult, setBlockSearchResult] = useState(null)
  const [prototypeSearchResult, setPrototypeSearchResult] = useState(null)
  const [vectorSearchMinSimilarity, setVectorSearchMinSimilarity] = useState('0.72')
  const [savedVectorSearchMinSimilarity, setSavedVectorSearchMinSimilarity] =
    useState('0.72')
  const [vectorSearchPerBlockLimit, setVectorSearchPerBlockLimit] = useState('10')
  const [vectorSearchTopResultsLimit, setVectorSearchTopResultsLimit] = useState('10')

  const fetchPrototypes = async () => {
    try {
      const data = await PagePrototypeService.list()
      setPrototypes(data || [])
    } catch (err) {
      console.error('Failed to load prototypes', err)
    }
  }

  useEffect(() => {
    fetchPrototypes()
  }, [])

  useEffect(() => {
    let isMounted = true

    const fetchSettings = async () => {
      try {
        const settings = await PagePrototypeService.getSettings()
        const nextThreshold = String(
          settings?.matched_intents_min_similarity ?? '0.72'
        )

        if (!isMounted) {
          return
        }

        setVectorSearchMinSimilarity(nextThreshold)
        setSavedVectorSearchMinSimilarity(nextThreshold)
      } catch (err) {
        console.error('Failed to load page prototype settings', err)
      }
    }

    fetchSettings()

    return () => {
      isMounted = false
    }
  }, [])

  const searchablePrototypes = useMemo(
    () =>
      (prototypes || []).map((prototype) => ({
        ...prototype,
        __searchText: buildPrototypeSearchText(prototype),
      })),
    [prototypes]
  )

  const filteredPrototypes = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return searchablePrototypes.filter((prototype) => {
      const isCandidate = getPrototypeCategory(prototype) === 'Candidate'
      if (isCandidate !== showCandidatePrototypes) {
        return false
      }

      if (!query) {
        return true
      }

      return prototype.__searchText.includes(query)
    })
  }, [searchQuery, searchablePrototypes, showCandidatePrototypes])

  const approvedBlockTypeOptions = useMemo(
    () => collectApprovedBlockTypes(prototypes),
    [prototypes]
  )

  const sortedPrototypes = useMemo(() => {
    const list = [...filteredPrototypes]

    const getSortValue = (prototype) => {
      switch (sortBy) {
        case 'name':
          return String(prototype?.name || '').toLowerCase()
        case 'intent':
          return String(prototype?.intent || '').toLowerCase()
        case 'overview':
          return String(extractOverview(prototype) || '').toLowerCase()
        case 'blocks_count':
          return Number(prototype?.blocks?.length || 0)
        case 'updated_at':
          return prototype?.updated_at ? new Date(prototype.updated_at).getTime() : 0
        default:
          return String(prototype?.name || '').toLowerCase()
      }
    }

    list.sort((a, b) => {
      const aValue = getSortValue(a)
      const bValue = getSortValue(b)
      let result = 0

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        result = aValue - bValue
      } else {
        result = String(aValue).localeCompare(String(bValue))
      }

      return sortDirection === 'asc' ? result : -result
    })

    return list
  }, [filteredPrototypes, sortBy, sortDirection])

  useEffect(() => {
    setTablePage(0)
  }, [searchQuery, rowsPerPage, sortBy, sortDirection, showCandidatePrototypes])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sortedPrototypes.length / rowsPerPage) - 1)
    if (tablePage > maxPage) {
      setTablePage(maxPage)
    }
  }, [sortedPrototypes.length, rowsPerPage, tablePage])

  const pagedPrototypes = useMemo(() => {
    const start = tablePage * rowsPerPage
    return sortedPrototypes.slice(start, start + rowsPerPage)
  }, [sortedPrototypes, tablePage, rowsPerPage])

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortBy(column)
    setSortDirection(column === 'updated_at' ? 'desc' : 'asc')
  }

  const handleFormChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }))
  }

  const handleVectorSearchBlockChange = (index, field) => (event) => {
    const value = event.target.value
    setVectorSearchBlocks((prev) =>
      prev.map((block, idx) =>
        idx === index ? { ...block, [field]: value } : block
      )
    )
  }

  const addVectorSearchBlock = () => {
    setVectorSearchBlocks((prev) => [...prev, emptySearchBlock(prev.length + 1)])
  }

  const removeVectorSearchBlock = (index) => {
    if (vectorSearchBlocks.length === 1) {
      setVectorSearchBlocks([emptySearchBlock(1)])
      return
    }

    setVectorSearchBlocks((prev) =>
      prev
        .filter((_, idx) => idx !== index)
        .map((block, idx) => ({
          ...block,
          id: `search-${idx + 1}`,
        }))
    )
  }

  const buildVectorSearchPayloadBlocks = () => {
    const normalized = vectorSearchBlocks
      .filter((block) => block?.kind !== 'overview')
      .map((block) => ({
        title: String(block?.title || '').trim(),
        description: String(block?.description || '').trim(),
      }))
      .filter((block) => block.title || block.description)

    if (!normalized.length) {
      throw new Error('Add at least one search block with title or description')
    }

    return normalized
  }

  const persistVectorSearchMinSimilarity = async () => {
    const normalizedValue = Number.parseFloat(vectorSearchMinSimilarity)

    if (Number.isNaN(normalizedValue)) {
      setVectorSearchMinSimilarity(savedVectorSearchMinSimilarity)
      return
    }

    try {
      const settings = await PagePrototypeService.updateSettings({
        matched_intents_min_similarity: normalizedValue,
      })
      const nextThreshold = String(
        settings?.matched_intents_min_similarity ?? normalizedValue
      )
      setVectorSearchMinSimilarity(nextThreshold)
      setSavedVectorSearchMinSimilarity(nextThreshold)
    } catch (err) {
      console.error('Failed to update page prototype settings', err)
      setVectorSearchError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to save matched intents similarity threshold'
      )
      setVectorSearchMinSimilarity(savedVectorSearchMinSimilarity)
    }
  }

  const runBlockVectorSearch = async () => {
    setVectorSearchError('')
    setBlockSearchResult(null)

    try {
      const searchBlocks = buildVectorSearchPayloadBlocks()
      const parsedMinSimilarity = Number.parseFloat(vectorSearchMinSimilarity)
      setVectorSearchLoading(true)
      const result = await PagePrototypeService.searchBlocks({
        ...searchBlocks[0],
        limit: Number.parseInt(vectorSearchTopResultsLimit, 10) || 10,
        min_similarity: Number.isNaN(parsedMinSimilarity)
          ? Number.parseFloat(savedVectorSearchMinSimilarity) || 0.72
          : parsedMinSimilarity,
      })
      setBlockSearchResult(result)
    } catch (err) {
      console.error('Failed to run block vector search', err)
      setVectorSearchError(
        err?.response?.data?.error || err?.message || 'Failed to search blocks'
      )
    } finally {
      setVectorSearchLoading(false)
    }
  }

  const runPrototypeVectorSearch = async () => {
    setVectorSearchError('')
    setPrototypeSearchResult(null)

    try {
      const searchBlocks = buildVectorSearchPayloadBlocks()
      const parsedMinSimilarity = Number.parseFloat(vectorSearchMinSimilarity)
      setVectorSearchLoading(true)
      const result = await PagePrototypeService.searchPrototypes({
        blocks: searchBlocks,
        limit: Number.parseInt(vectorSearchTopResultsLimit, 10) || 10,
        per_block_limit: Number.parseInt(vectorSearchPerBlockLimit, 10) || 10,
        min_similarity: Number.isNaN(parsedMinSimilarity)
          ? Number.parseFloat(savedVectorSearchMinSimilarity) || 0.72
          : parsedMinSimilarity,
        approved_only: true,
      })
      setPrototypeSearchResult(result)
    } catch (err) {
      console.error('Failed to run prototype vector search', err)
      setVectorSearchError(
        err?.response?.data?.error || err?.message || 'Failed to search prototypes'
      )
    } finally {
      setVectorSearchLoading(false)
    }
  }

  const clearVectorSearch = () => {
    setVectorSearchBlocks([emptySearchBlock(1)])
    setVectorSearchError('')
    setBlockSearchResult(null)
    setPrototypeSearchResult(null)
    setVectorSearchMinSimilarity(savedVectorSearchMinSimilarity)
    setVectorSearchPerBlockLimit('10')
    setVectorSearchTopResultsLimit('10')
  }

  const useCurrentPrototypeAsQuery = () => {
    setVectorSearchBlocks(toSearchQueryBlocksFromEditor(form, blocks))
    setVectorSearchError('')
    setBlockSearchResult(null)
    setPrototypeSearchResult(null)
  }

  const resetEditor = () => {
    setForm({ ...emptyForm })
    setBlocks([emptyBlock(1)])
    setScreenshotFile(null)
    setDomJsonFile(null)
    setEditingPrototypeId(null)
    setError('')
  }

  const startEditPrototype = (prototype) => {
    setEditingPrototypeId(prototype.id)
    setForm({
      name: prototype?.name || '',
      intent: prototype?.intent || '',
      screenshot_url: prototype?.screenshot_url || '',
      dom_snapshot_url: prototype?.dom_snapshot_url || '',
      page_overview: extractOverview(prototype),
    })
    setBlocks(toEditableBlocks(prototype))
    setScreenshotFile(null)
    setDomJsonFile(null)
    setError('')
  }

  const handleBlockChange = (index, field) => (event) => {
    const value = event.target.value
    setBlocks((prev) =>
      prev.map((block, idx) =>
        idx === index ? { ...block, [field]: value } : block
      )
    )
  }

  const handleBlockIntentChange = (index, field) => (event) => {
    const value = event.target.value
    setBlocks((prev) =>
      prev.map((block, idx) =>
        idx === index
          ? {
              ...block,
              intent: {
                ...normalizeBlockIntent(block?.intent),
                [field]: value,
              },
            }
          : block
      )
    )
  }

  const addBlock = () => {
    setBlocks((prev) => [...prev, emptyBlock(prev.length + 1)])
  }

  const removeBlock = async (index) => {
    const block = blocks[index]
    if (editingPrototypeId && block?.id) {
      const confirmed = window.confirm('Delete this block from the prototype?')
      if (!confirmed) return

      setLoading(true)
      setError('')
      try {
        const updated = await PagePrototypeService.deleteBlock(
          editingPrototypeId,
          block.id
        )
        setEditingPrototypeId(updated.id)
        setForm({
          name: updated?.name || '',
          intent: updated?.intent || '',
          screenshot_url: updated?.screenshot_url || '',
          dom_snapshot_url: updated?.dom_snapshot_url || '',
          page_overview: extractOverview(updated),
        })
        setBlocks(toEditableBlocks(updated))
        await fetchPrototypes()
      } catch (err) {
        console.error('Failed to delete prototype block', err)
        setError(err?.response?.data?.error || 'Failed to delete prototype block')
      } finally {
        setLoading(false)
      }
      return
    }

    if (blocks.length === 1) return

    setBlocks((prev) =>
      prev
        .filter((_, idx) => idx !== index)
        .map((block, idx) => ({ ...block, sort_order: idx + 1 }))
    )
  }

  const toggleBlockStatus = async (index) => {
    const block = blocks[index]
    const currentStatus = block?.status || BLOCK_STATUS_DRAFT
    const nextStatus =
      currentStatus === BLOCK_STATUS_APPROVED
        ? BLOCK_STATUS_DRAFT
        : BLOCK_STATUS_APPROVED

    if (editingPrototypeId && block?.id) {
      setLoading(true)
      setError('')
      try {
        const updated = await PagePrototypeService.updateBlockStatus(
          editingPrototypeId,
          block.id,
          nextStatus
        )
        setEditingPrototypeId(updated.id)
        setForm({
          name: updated?.name || '',
          intent: updated?.intent || '',
          screenshot_url: updated?.screenshot_url || '',
          dom_snapshot_url: updated?.dom_snapshot_url || '',
          page_overview: extractOverview(updated),
        })
        setBlocks(toEditableBlocks(updated))
        await fetchPrototypes()
      } catch (err) {
        console.error('Failed to update block status', err)
        setError(err?.response?.data?.error || 'Failed to update block status')
      } finally {
        setLoading(false)
      }
      return
    }

    setBlocks((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, status: nextStatus } : item
      )
    )
  }

  const validate = () => {
    if (!form.name.trim()) {
      return 'Prototype name is required'
    }
    if (!blocks.length) {
      return 'At least one block is required'
    }
    const overview = form.page_overview.trim()
    if (overview && overview.length > MAX_BLOCK_DESCRIPTION_LENGTH) {
      return `Page overview exceeds ${MAX_BLOCK_DESCRIPTION_LENGTH} characters`
    }
    for (let i = 0; i < blocks.length; i += 1) {
      const block = blocks[i]
      if (!block.description.trim()) {
        return `Block ${i + 1} description is required`
      }
      if (block.description.length > MAX_BLOCK_DESCRIPTION_LENGTH) {
        return `Block ${i + 1} exceeds ${MAX_BLOCK_DESCRIPTION_LENGTH} characters`
      }
    }
    return ''
  }

  const handleSubmit = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    setError('')

    try {
      const payload = {
        ...form,
        page_overview: form.page_overview.trim() || null,
        schema_version: 'v1',
        status: 'draft',
        blocks: blocks.map((block, idx) => ({
          ...block,
          intent: toIntentPayload(block.intent),
          sort_order: idx + 1,
        })),
      }

      const saved = editingPrototypeId
        ? await PagePrototypeService.update(editingPrototypeId, payload)
        : await PagePrototypeService.create(payload)

      if (screenshotFile) {
        const safeName = screenshotFile.name.replace(/[^A-Za-z0-9._-]/g, '_')
        const safeFile = new File([screenshotFile], safeName, {
          type: screenshotFile.type,
        })
        await PagePrototypeService.uploadScreenshot(saved.id, safeFile)
      }

      if (domJsonFile) {
        const originalName = domJsonFile.name.replace(/[^A-Za-z0-9._-]/g, '_')
        const safeName = originalName.toLowerCase().endsWith('.json')
          ? originalName
          : `${originalName}.json`
        const safeFile = new File([domJsonFile], safeName, {
          type: domJsonFile.type || 'application/json',
        })
        await PagePrototypeService.uploadDomJson(saved.id, safeFile)
      }

      resetEditor()
      await fetchPrototypes()
    } catch (err) {
      console.error('Failed to save prototype', err)
      setError(err?.response?.data?.error || 'Failed to save prototype')
    } finally {
      setLoading(false)
    }
  }

  const handleBulkImport = async () => {
    if (!urlsFile) {
      setBulkError('Select a .txt/.csv file with one URL per line')
      return
    }
    if (!selectedProject?.id) {
      setBulkError('Select a project before importing page prototypes')
      return
    }

    setBulkError('')
    setBulkResult(null)
    setBulkLoading(true)

    try {
      const result = await PagePrototypeService.importUrlsFile(
        urlsFile,
        selectedProject.id
      )
      setBulkResult(result)
      await fetchPrototypes()
    } catch (err) {
      console.error('Failed to import URLs', err)
      setBulkError(err?.response?.data?.error || 'Failed to import URL file')
    } finally {
      setBulkLoading(false)
    }
  }

  const handleDeletePrototype = async (prototype) => {
    const confirmed = window.confirm(
      `Delete prototype "${prototype?.name || 'Unnamed'}" and all its blocks?`
    )
    if (!confirmed) return

    setLoading(true)
    setError('')
    try {
      await PagePrototypeService.deletePrototype(prototype.id)
      if (editingPrototypeId === prototype.id) {
        resetEditor()
      }
      await fetchPrototypes()
    } catch (err) {
      console.error('Failed to delete prototype', err)
      setError(err?.response?.data?.error || 'Failed to delete prototype')
    } finally {
      setLoading(false)
    }
  }

  if (!prototypesEnabled) {
    return (
      <Box className="pages rootWrapper">
        <Header />
        <Box className="srollContent" sx={{ minHeight: 0 }}>
          <Sidebar />
          <Box className="pagesMainContainer" sx={{ overflowY: 'auto !important' }}>
            <BreadcrumbsNav pageLabel="prototypes" />
            <Typography sx={{ mt: 2 }}>
              Page prototypes are not available for your account in production.
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box className="pages rootWrapper">
      <Helmet>
        <title>Treegress - Page Prototypes</title>
      </Helmet>
      <Header />
      <Box className="srollContent" sx={{ minHeight: 0 }}>
        <Sidebar />
        <Box
          className="pagesMainContainer"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden !important',
          }}
        >
          <BreadcrumbsNav pageLabel="prototypes" />
          <Box sx={{ mt: 2, pb: 4, pr: 0.5, overflowY: 'auto', minHeight: 0, flex: 1 }}>
            <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Vector search debug
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Manually enter one or more page blocks and test the pgvector search
                  against approved block prototypes. Use block search to inspect
                  matches and prototype search to see aggregated page-level ranking.
                  The min similarity setting here is shared with journey generation.
                </Typography>

                {vectorSearchBlocks.map((block, index) => (
                  <Paper
                    key={block.id}
                    variant="outlined"
                    sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)' }}
                  >
                    <Stack spacing={2}>
                      <Typography variant="subtitle2">
                        Query block {index + 1}
                      </Typography>
                      <Tooltip title="Short label for the functional block you want to search by.">
                        <TextField
                          label="Title"
                          value={block.title}
                          onChange={handleVectorSearchBlockChange(index, 'title')}
                          fullWidth
                        />
                      </Tooltip>
                      <Tooltip title="Describe what this block does. This text is converted into an embedding and compared against stored prototype blocks.">
                        <TextField
                          label="Description"
                          value={block.description}
                          onChange={handleVectorSearchBlockChange(index, 'description')}
                          multiline
                          minRows={3}
                          maxRows={8}
                          fullWidth
                        />
                      </Tooltip>
                      <Stack direction="row" spacing={2}>
                        <Tooltip title="Remove this query block from the current search request.">
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => removeVectorSearchBlock(index)}
                          >
                            Remove query block
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Tooltip title="Minimum similarity score required for a block match to appear in UI block search and in matched_intents during journey generation. Saved on blur.">
                    <TextField
                      label="Min similarity"
                      type="number"
                      value={vectorSearchMinSimilarity}
                      onChange={(event) =>
                        setVectorSearchMinSimilarity(event.target.value)
                      }
                      onBlur={persistVectorSearchMinSimilarity}
                      inputProps={{
                        min: 0,
                        max: 1,
                        step: 0.01,
                      }}
                      sx={{ width: { xs: '100%', md: 160 } }}
                    />
                  </Tooltip>
                  <Tooltip title="For each query block, inspect this many nearest block candidates before aggregating by page prototype.">
                    <TextField
                      label="Per-block limit"
                      type="number"
                      value={vectorSearchPerBlockLimit}
                      onChange={(event) =>
                        setVectorSearchPerBlockLimit(event.target.value)
                      }
                      inputProps={{
                        min: 1,
                        max: 100,
                        step: 1,
                      }}
                      sx={{ width: { xs: '100%', md: 160 } }}
                    />
                  </Tooltip>
                  <Tooltip title="Maximum number of rows returned in the final result list.">
                    <TextField
                      label="Top results limit"
                      type="number"
                      value={vectorSearchTopResultsLimit}
                      onChange={(event) =>
                        setVectorSearchTopResultsLimit(event.target.value)
                      }
                      inputProps={{
                        min: 1,
                        max: 100,
                        step: 1,
                      }}
                      sx={{ width: { xs: '100%', md: 160 } }}
                    />
                  </Tooltip>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Tooltip title="Add another functional block to the current query. Use this when the page should be matched by multiple blocks, not just one.">
                    <Button variant="outlined" onClick={addVectorSearchBlock}>
                      Add query block
                    </Button>
                  </Tooltip>
                  <Tooltip title="Copy the current editor content into the search form using all non-empty functional blocks. Useful when you are editing a prototype and want to test its nearest neighbors.">
                    <Button
                      variant="outlined"
                      onClick={useCurrentPrototypeAsQuery}
                      disabled={vectorSearchLoading}
                    >
                      Use current prototype as query
                    </Button>
                  </Tooltip>
                  <Tooltip title="Run nearest-neighbor search for a single block only. This uses the first non-empty query block and applies the same min similarity threshold used by journey generation.">
                    <Button
                      variant="outlined"
                      onClick={runBlockVectorSearch}
                      disabled={vectorSearchLoading}
                    >
                      {vectorSearchLoading ? 'Searching...' : 'Search similar blocks'}
                    </Button>
                  </Tooltip>
                  <Tooltip title="Run page-level ranking. All filled query blocks are searched, then grouped by prototype and scored using similarity plus coverage.">
                    <Button
                      variant="contained"
                      onClick={runPrototypeVectorSearch}
                      disabled={vectorSearchLoading}
                    >
                      {vectorSearchLoading ? 'Searching...' : 'Search similar prototypes'}
                    </Button>
                  </Tooltip>
                  <Tooltip title="Reset all query blocks, search parameters, and current search results.">
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={clearVectorSearch}
                      disabled={vectorSearchLoading}
                    >
                      Clear
                    </Button>
                  </Tooltip>
                </Stack>

                {vectorSearchError ? (
                  <Typography color="error">{vectorSearchError}</Typography>
                ) : null}

                {blockSearchResult ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      Similar blocks ({blockSearchResult?.matches?.length || 0})
                    </Typography>
                    {blockSearchResult?.matches?.length ? (
                      blockSearchResult.matches.map((match) => (
                        <Paper key={match.id} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={1}>
                            <Typography variant="body2">
                              <strong>{match.prototype_name}</strong> /{' '}
                              {match.title || match.block_key}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              similarity {Number(match.similarity || 0).toFixed(4)} •{' '}
                              {match.block_type || 'no type'} • {match.status}
                              {match.was_interacted ? ' • interacted' : ''}
                            </Typography>
                            <Typography variant="body2">
                              {shorten(match.description, 240)}
                            </Typography>
                            {match.intent ? (
                              <Typography variant="caption" color="text.secondary">
                                intent: {formatBlockIntentPreview(match.intent, 240)}
                              </Typography>
                            ) : null}
                          </Stack>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        No block matches found.
                      </Typography>
                    )}
                  </Stack>
                ) : null}

                {prototypeSearchResult ? (
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      Similar prototypes ({prototypeSearchResult?.results?.length || 0})
                    </Typography>
                    {prototypeSearchResult?.results?.length ? (
                      prototypeSearchResult.results.map((result) => (
                        <Paper key={result.prototype_id} variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={1}>
                            <Typography variant="body2">
                              <strong>{result.prototype_name}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              score {Number(result.score || 0).toFixed(4)} • avg{' '}
                              {Number(result.avg_similarity || 0).toFixed(4)} •
                              boosted avg{' '}
                              {Number(
                                result.boosted_avg_similarity || result.avg_similarity || 0
                              ).toFixed(4)} • coverage {Number(result.coverage || 0).toFixed(2)} •
                              interacted {result.interacted_matches_count || 0}/
                              {result.total_query_blocks} •
                              matches {result.matched_blocks_count}/{result.total_query_blocks}
                            </Typography>
                            {(result.matches || []).map((match) => (
                              <Box key={`${result.prototype_id}-${match.query_block_index}`}>
                                <Typography variant="caption" color="text.secondary">
                                  query block {match.query_block_index + 1}: {' '}
                                  {match.query_block?.title || 'Untitled'} {'->'}{' '}
                                  {match.matched_block?.title || match.matched_block?.block_key}
                                  {' '}({Number(match.similarity || 0).toFixed(4)})
                                  {match.matched_block?.was_interacted
                                    ? ' • interacted'
                                    : ''}
                                </Typography>
                              </Box>
                            ))}
                            {result.intents?.length ? (
                              <Typography variant="caption" color="text.secondary">
                                intents:{' '}
                                {shorten(
                                  result.intents
                                    .map((intent) => formatBlockIntentPreview(intent, 320))
                                    .join(' || '),
                                  320
                                )}
                              </Typography>
                            ) : null}
                          </Stack>
                        </Paper>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        No prototype matches passed the similarity threshold.
                      </Typography>
                    )}
                  </Stack>
                ) : null}
              </Stack>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Bulk import from URL file
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Upload a .txt/.csv file with one URL per line. The system will
                  fetch each page, split it into functional blocks, generate
                  descriptions, build embeddings, and save prototypes in
                  background tasks.
                </Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Button variant="outlined" component="label">
                    Select URL file
                    <input
                      type="file"
                      hidden
                      accept=".txt,.csv,text/plain,text/csv"
                      onChange={(event) => {
                        const [file] = event.target.files || []
                        setUrlsFile(file || null)
                      }}
                    />
                  </Button>
                  {urlsFile ? (
                    <Typography variant="body2">
                      Selected: {urlsFile.name}
                    </Typography>
                  ) : null}
                </Stack>

                <Button
                  variant="contained"
                  onClick={handleBulkImport}
                  disabled={bulkLoading}
                >
                  {bulkLoading ? 'Importing...' : 'Import URL file'}
                </Button>

                {bulkError ? <Typography color="error">{bulkError}</Typography> : null}

                {bulkResult ? (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      Queued: {bulkResult.queued_count ?? 0} / {bulkResult.total_urls}
                    </Typography>
                    {bulkResult.failed_count > 0 ? (
                      <Stack spacing={0.5}>
                        {bulkResult.results
                          .filter((item) => item.status === 'failed')
                          .slice(0, 10)
                          .map((item) => (
                            <Typography
                              key={item.url}
                              variant="caption"
                              color="error"
                            >
                              {item.url}: {item.error}
                            </Typography>
                          ))}
                      </Stack>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {editingPrototypeId
                  ? 'Edit Page Prototype (manual)'
                  : 'Create Page Prototype (manual)'}
              </Typography>
              <Stack spacing={2}>
                {editingPrototypeId ? (
                  <Typography variant="caption" color="text.secondary">
                    Prototype ID: {editingPrototypeId}
                  </Typography>
                ) : null}
                <TextField
                  label="Prototype name"
                  value={form.name}
                  onChange={handleFormChange('name')}
                  required
                />
                <TextField
                  label="Intent"
                  value={form.intent}
                  onChange={handleFormChange('intent')}
                  multiline
                  minRows={2}
                  maxRows={12}
                />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Screenshot URL"
                    value={form.screenshot_url}
                    onChange={handleFormChange('screenshot_url')}
                    fullWidth
                  />
                  <TextField
                    label="DOM snapshot URL"
                    value={form.dom_snapshot_url}
                    onChange={handleFormChange('dom_snapshot_url')}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Button variant="outlined" component="label">
                    Upload screenshot
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(event) => {
                        const [file] = event.target.files || []
                        setScreenshotFile(file || null)
                      }}
                    />
                  </Button>
                  <Button variant="outlined" component="label">
                    Upload DOM JSON
                    <input
                      type="file"
                      hidden
                      accept=".json,application/json"
                      onChange={(event) => {
                        const [file] = event.target.files || []
                        setDomJsonFile(file || null)
                      }}
                    />
                  </Button>
                  {screenshotFile ? (
                    <Typography variant="body2">
                      Selected: {screenshotFile.name}
                    </Typography>
                  ) : null}
                  {domJsonFile ? (
                    <Typography variant="body2">
                      Selected DOM JSON: {domJsonFile.name}
                    </Typography>
                  ) : null}
                </Stack>
                <TextField
                  label={`Page overview (${form.page_overview.length}/${MAX_BLOCK_DESCRIPTION_LENGTH})`}
                  value={form.page_overview}
                  onChange={handleFormChange('page_overview')}
                  multiline
                  minRows={3}
                  maxRows={10}
                />
                <Divider />
                <Typography variant="subtitle1">Blocks</Typography>
                <Stack spacing={2}>
                  {blocks.map((block, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)' }}
                    >
                      <Stack spacing={2}>
                        <Typography variant="caption" color="text.secondary">
                          Block ID: {block.id || 'Not saved yet'}
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            label="Block key"
                            value={block.block_key}
                            onChange={handleBlockChange(index, 'block_key')}
                            fullWidth
                          />
                          <Autocomplete
                            freeSolo
                            options={approvedBlockTypeOptions}
                            value={block.block_type || ''}
                            onChange={(_, value) => {
                              const nextValue = String(value || '')
                              setBlocks((prev) =>
                                prev.map((item, idx) =>
                                  idx === index
                                    ? { ...item, block_type: nextValue }
                                    : item
                                )
                              )
                            }}
                            onInputChange={(_, value, reason) => {
                              if (reason === 'reset') {
                                return
                              }

                              setBlocks((prev) =>
                                prev.map((item, idx) =>
                                  idx === index
                                    ? { ...item, block_type: value }
                                    : item
                                )
                              )
                            }}
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Block type"
                                helperText="Select an approved block type or enter a new one"
                              />
                            )}
                          />
                        </Stack>
                        <TextField
                          label="Title"
                          value={block.title}
                          onChange={handleBlockChange(index, 'title')}
                        />
                        <TextField
                          label={`Description (${block.description.length}/${MAX_BLOCK_DESCRIPTION_LENGTH})`}
                          value={block.description}
                          onChange={handleBlockChange(index, 'description')}
                          multiline
                          minRows={3}
                          maxRows={10}
                        />
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                          <TextField
                            label="Positive intent"
                            value={normalizeBlockIntent(block.intent).positive}
                            onChange={handleBlockIntentChange(index, 'positive')}
                            multiline
                            minRows={2}
                            maxRows={8}
                            fullWidth
                          />
                          <TextField
                            label="Negative intent"
                            value={normalizeBlockIntent(block.intent).negative}
                            onChange={handleBlockIntentChange(index, 'negative')}
                            multiline
                            minRows={2}
                            maxRows={8}
                            fullWidth
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          color={
                            (block.status || BLOCK_STATUS_DRAFT) ===
                            BLOCK_STATUS_APPROVED
                              ? 'success.main'
                              : 'text.secondary'
                          }
                        >
                          Status: {block.status || BLOCK_STATUS_DRAFT}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={block.was_interacted ? 'info.main' : 'text.secondary'}
                        >
                          Interacted (heuristic): {block.was_interacted ? 'yes' : 'no'}
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          <Button
                            variant="outlined"
                            color={
                              (block.status || BLOCK_STATUS_DRAFT) ===
                              BLOCK_STATUS_APPROVED
                                ? 'inherit'
                                : 'success'
                            }
                            onClick={() => toggleBlockStatus(index)}
                            disabled={loading}
                          >
                            {(block.status || BLOCK_STATUS_DRAFT) ===
                            BLOCK_STATUS_APPROVED
                              ? 'Set draft'
                              : 'Approve block'}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => removeBlock(index)}
                            disabled={
                              loading ||
                              (!editingPrototypeId && blocks.length === 1)
                            }
                          >
                            {editingPrototypeId && block.id
                              ? 'Delete block'
                              : 'Remove block'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <Button variant="outlined" onClick={addBlock}>
                  Add block
                </Button>
                {error ? <Typography color="error">{error}</Typography> : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading
                      ? 'Saving...'
                      : editingPrototypeId
                        ? 'Save changes'
                        : 'Save prototype'}
                  </Button>
                  {editingPrototypeId ? (
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={resetEditor}
                      disabled={loading}
                    >
                      Cancel editing
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Existing prototypes
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Search across all fields"
                    placeholder="prototype id, block id, name, intent, block title/type/description, URLs, status, source, interacted..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    fullWidth
                  />
                  <Tooltip title="Switch between the curated knowledge base and journey-derived candidate prototypes.">
                    <Paper
                      variant="outlined"
                      sx={{
                        px: 2,
                        py: 1.5,
                        minWidth: { xs: '100%', md: 300 },
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%' }}
                      >
                        <Typography
                          variant="body2"
                          color={showCandidatePrototypes ? 'text.secondary' : 'text.primary'}
                        >
                          Knowledge base
                        </Typography>
                        <Switch
                          checked={showCandidatePrototypes}
                          onChange={(event) =>
                            setShowCandidatePrototypes(event.target.checked)
                          }
                        />
                        <Typography
                          variant="body2"
                          color={showCandidatePrototypes ? 'text.primary' : 'text.secondary'}
                        >
                          Candidates
                        </Typography>
                      </Stack>
                    </Paper>
                  </Tooltip>
                </Stack>
                {prototypes.length === 0 ? (
                  <Typography color="text.secondary">No prototypes yet.</Typography>
                ) : filteredPrototypes.length === 0 ? (
                  <Typography color="text.secondary">
                    No prototypes match your search.
                  </Typography>
                ) : (
                  <Paper variant="outlined">
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                              <TableSortLabel
                                active={sortBy === 'name'}
                                direction={sortBy === 'name' ? sortDirection : 'asc'}
                                onClick={() => handleSortChange('name')}
                              >
                                Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'intent' ? sortDirection : false}>
                              <TableSortLabel
                                active={sortBy === 'intent'}
                                direction={sortBy === 'intent' ? sortDirection : 'asc'}
                                onClick={() => handleSortChange('intent')}
                              >
                                Intent
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'overview' ? sortDirection : false}>
                              <TableSortLabel
                                active={sortBy === 'overview'}
                                direction={sortBy === 'overview' ? sortDirection : 'asc'}
                                onClick={() => handleSortChange('overview')}
                              >
                                Overview
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'blocks_count' ? sortDirection : false}>
                              <TableSortLabel
                                active={sortBy === 'blocks_count'}
                                direction={sortBy === 'blocks_count' ? sortDirection : 'asc'}
                                onClick={() => handleSortChange('blocks_count')}
                              >
                                Blocks
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Interacted</TableCell>
                            <TableCell>Screenshot</TableCell>
                            <TableCell>DOM</TableCell>
                            <TableCell sortDirection={sortBy === 'updated_at' ? sortDirection : false}>
                              <TableSortLabel
                                active={sortBy === 'updated_at'}
                                direction={sortBy === 'updated_at' ? sortDirection : 'desc'}
                                onClick={() => handleSortChange('updated_at')}
                              >
                                Updated
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pagedPrototypes.map((prototype) => {
                            const overview = extractOverview(prototype)
                            const interactedBlocksCount = countInteractedBlocks(prototype)
                            const approvedBlocksCount = countApprovedBlocks(prototype)
                            return (
                              <TableRow key={prototype.id} hover>
                                <TableCell sx={{ minWidth: 180, maxWidth: 260 }}>
                                  <Typography variant="body2">
                                    {shorten(prototype.name, 120)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {prototype.id}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ minWidth: 220, maxWidth: 320 }}>
                                  <Typography variant="body2">
                                    {shorten(prototype.intent, 160)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ minWidth: 220, maxWidth: 320 }}>
                                  <Typography variant="body2">
                                    {shorten(overview, 160)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {prototype.blocks?.length || 0}
                                  {prototype.blocks?.length
                                    ? ` (${approvedBlocksCount} approved)`
                                    : ''}
                                </TableCell>
                                <TableCell sx={{ minWidth: 150 }}>
                                  <Typography variant="body2">
                                    {getPrototypeCategory(prototype)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ minWidth: 180, maxWidth: 240 }}>
                                  <Typography variant="body2">
                                    {shorten(formatPrototypeSource(prototype), 80)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {interactedBlocksCount
                                    ? `${interactedBlocksCount} marked`
                                    : '—'}
                                </TableCell>
                                <TableCell>
                                  {prototype.screenshot_url ? (
                                    <Button
                                      size="small"
                                      href={prototype.screenshot_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Open
                                    </Button>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell>
                                  {prototype.dom_snapshot_url ? (
                                    <Button
                                      size="small"
                                      href={prototype.dom_snapshot_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Open
                                    </Button>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {prototype.updated_at
                                    ? new Date(prototype.updated_at).toLocaleString()
                                    : '—'}
                                </TableCell>
                                <TableCell align="right">
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent="flex-end"
                                  >
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => startEditPrototype(prototype)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => handleDeletePrototype(prototype)}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={sortedPrototypes.length}
                      page={tablePage}
                      onPageChange={(_, nextPage) => setTablePage(nextPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10))
                      }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                  </Paper>
                )}
              </Stack>
            </Paper>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PagePrototypes
